import { Body, Heading, Icon } from '@assembly-js/design-system';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Channel as ChannelType } from 'stream-chat';

import FilesClient from 'src/clients/FilesClient';
import {
  APP_BUILDER_SESSION_PATH_PREFIX,
  CLIENT_APPS_PAGE,
  ENTITIES,
  FOLDER_FILE_KEY,
  PAYMENTS_INVOICES_PAGE,
  User,
} from 'src/constants';
import { AssistantProvider, useFeatureFlags } from 'src/context';
import { InboxPageContext } from 'src/context/inboxPageContext';
import {
  InboxNotificationDetailItem,
  NotificationEventType,
} from 'src/entities/Notifications';
import { InternalChat } from 'src/features/assistant/InternalChat';
import { InternalChatError } from 'src/features/assistant/InternalChatError';
import { ChannelMention } from 'src/features/channel/ChannelMention';
import history from 'src/history';
import { useContextBar } from 'src/hooks/useContextBar';
import { useExportFormResponse } from 'src/hooks/useExportFormResponse';
import useNavigate from 'src/hooks/useNavigate';
import { useNotificationIframeUrl } from 'src/hooks/useNotificationIframeUrl';
import { useProgressBatchDelete } from 'src/hooks/useProgressBatchDelete';
import { useProgressDownload } from 'src/hooks/useProgressDownload';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import Button from 'src/legacy/components/Button';
import { RegularCardBox } from 'src/legacy/components/Cards/RegularCardBox';
import { useChatClient } from 'src/legacy/components/Chat/useChatClient';
import {
  getExtensionFromFileKey,
  getFileNameFromFileKey,
} from 'src/legacy/components/Files/helpers';
import ActionButtonModal from 'src/legacy/components/Modals/ActionButtonModal';
import MemoBaseTypography from 'src/legacy/components/Text/BaseTypography';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { useGetInboxNotificationDetailsQuery } from 'src/services/api/inboxApi';
import { RootState } from 'src/store';
import { downloadFiles } from 'src/store/files/actions';
import { NotificationGroups } from 'src/store/notifications/types';
import { alertSnackbar } from 'src/store/ui/actions';
import { FileUtils } from 'src/utils';
import { ensureApiError } from 'src/utils/Errors';
import S3Utils from 'src/utils/S3Utils';

import { RightSidebarToggle } from '../../../components/RightSidebarToggle';
import { ChannelContentToolbar } from '../Channels/ChannelContentToolbar';
import { FormResponseDetail } from '../FormsV2/FormResponseDetail';
import { LoadingWrapper } from '../Loading';

import { InboxEmptyState } from './InboxEmptyState';
import { InboxNotificationTable } from './InboxNotificationTable';
import { getFormCompletedResourceIds } from './InboxSidebar';
import { NotificationDetailsContainer } from './NotificationDetailsContainer';
import { NotificationDetailsHeaderActions } from './NotificationDetailsHeaderActions';
import { NotificationPdfViewer } from './NotificationPdfViewer';
import { LINK_FILE_EXTENSION } from './NotificationTableActionRenderer';
import { useInboxNotification } from './useInboxNotification';

const LegacyNotificationTypeToNotificationGroup: Partial<
  Record<NotificationGroups, NotificationEventType>
> = {
  [NotificationGroups.FileUploadNotification]: 'files.new',
  [NotificationGroups.EsigRequestCompleteNotifications]: 'esign.completed',
};

const invoicePreviewNotificationEvents: NotificationEventType[] = [
  'invoice.paid',
  'invoice.processing',
  'invoice.paymentFailed',
];

export interface NotificationTableRowData extends User {
  rowId: string;
  name: string;
  location: string;
  channelId: string;
  userName: string;
  isDeleted?: boolean;
  fileExtension?: string;
  // these fields are used for file preview
  fileIdentityId?: string;
  fileKey?: string;
  fileUrl?: string; // used to open links
}

export const DELETED_FILE_NOTIFICATION_STATUS = '0_CANCELLED';

// clientFacing is a string cta param ('true'/'false') set by the backend from
// builder.Surfaces.ClientPath — missing/absent defaults to the internal copy.
export function isAppReadyClientFacing(
  ctaParams: Record<string, string> | undefined,
): boolean {
  return ctaParams?.clientFacing === 'true';
}

// The app-ready copy is the one notification body designed as rich text, so its
// strings carry **emphasis** markers rather than being pre-split per fragment —
// the emphasised run sits mid-sentence in some variants.
export function renderEmphasis(text: string): React.ReactNode[] {
  return text.split(/\*\*(.+?)\*\*/g).map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="font-semibold">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

export const InboxContent = () => {
  const { selectedNotification, setSelectedNotification } =
    React.useContext(InboxPageContext);
  const { deleteNotification } = useInboxNotification();
  const { navigate } = useNavigate();
  const { EnableMessageAppNotification } = useFeatureFlags();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isClient = useAppSelector((state: RootState) => state.user.isClient);

  const isFileNotification =
    selectedNotification?.notificationEventType === 'files.new' ||
    selectedNotification?.notificationEventType === 'files.created';
  const isTableBasedNotification =
    isFileNotification ||
    selectedNotification?.notificationGroup ===
      NotificationGroups.EsigRequestCompleteNotifications;

  const isChannelMentioned =
    EnableMessageAppNotification &&
    selectedNotification?.notificationEventType === 'channel.mentioned';

  const previewContainerRef = React.useRef<HTMLDivElement>(null);

  const isFileUploadNotification =
    selectedNotification &&
    selectedNotification.notificationGroup === ENTITIES.NOTIFY_FILE_UPLOADED;

  const [notificationTableRowData, setNotificationTableRowData] =
    React.useState<NotificationTableRowData[]>([]);
  const {
    data: selectedNotificationDetails,
    isFetching: selectedNotificationDetailsLoading,
    refetch: refetchSelectedNotificationDetails,
  } = useGetInboxNotificationDetailsQuery(
    {
      id: encodeURIComponent(selectedNotification?.id || ''),
    },
    {
      skip: !selectedNotification?.id,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: { extensionsSettings } = {} } = useGetInstallsQuery();
  const { url: notificationUrl, isLoading: notificationUrlLoading } =
    useNotificationIframeUrl({
      resourceId: selectedNotificationDetails?.resourceId,
      notificationId: selectedNotification?.id,
    });

  const [
    filesNotificationDetailsDataReady,
    setFilesNotificationDetailsDataReady,
  ] = React.useState(false);
  // selectedNotificationDetails can still hold the previously selected
  // notification's data while the new one is fetching; the app-ready pane
  // derives its copy and destination from the details, so treat that stale
  // window as loading rather than rendering the wrong variant.
  const isAppReadyDetailsStale =
    selectedNotification?.notificationEventType === 'customApp.buildComplete' &&
    selectedNotificationDetails?.id !== selectedNotification?.id;
  const isNotificationContentLoading =
    selectedNotificationDetailsLoading ||
    isAppReadyDetailsStale ||
    (!filesNotificationDetailsDataReady &&
      (selectedNotification?.notificationEventType === 'files.new' ||
        selectedNotification?.notificationEventType === 'files.created')) ||
    (notificationUrlLoading &&
      selectedNotification?.notificationEventType === 'customApp.action');
  const chatClient = useChatClient();
  const [internalChannelData, setInternalChannelData] = React.useState<
    | {
        channel: ChannelType;
        recipientId: string;
        recipientType: 'client' | 'company';
        messageId: string;
      }
    | undefined
  >(undefined);
  const [internalChannelError, setInternalChannelError] = React.useState(false);
  const [messagingChannelData, setMessagingChannelData] = React.useState<
    | {
        notificationId: string;
        recipientId: string;
        recipientType: 'client' | 'company';
      }
    | undefined
  >(undefined);
  const [messagingChannelError, setMessagingChannelError] =
    React.useState(false);

  const [invoiceS3AssetUrl, setInvoiceS3AssetUrl] = React.useState('');
  const [contractS3AssetUrl, setContractS3AssetUrl] = React.useState('');

  const [selectedRowIds, setSelectedRowIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [isBatchDeleting, setIsBatchDeleting] = React.useState(false);

  // Reset bulk-selection state when the user switches notifications. Adjusting
  // state during render (vs. an effect that runs after commit) avoids an extra
  // render with stale selection. https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevNotificationId, setPrevNotificationId] = React.useState(
    selectedNotification?.id,
  );
  if (selectedNotification?.id !== prevNotificationId) {
    setPrevNotificationId(selectedNotification?.id);
    setSelectedRowIds(new Set());
    setConfirmDeleteOpen(false);
  }

  const handleToggleRow = (rowId: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedRowIds(new Set());
  };

  const handleSelectAllRows = () => {
    setSelectedRowIds(
      new Set(
        notificationTableRowData
          .filter((row) => !row.isDeleted)
          .map((row) => row.rowId),
      ),
    );
  };

  const startBatchZipDownload = useProgressDownload(
    FilesClient.getBatchDownloadProgress,
  );
  const startBatchDeleteProgress = useProgressBatchDelete({
    onSuccess: refetchSelectedNotificationDetails,
  });

  const getSelectedRowsForBulk = () =>
    notificationTableRowData.filter(
      (row) => selectedRowIds.has(row.rowId) && !row.isDeleted,
    );

  // Single-file selections take the S3 fast path so the standard download
  // audit event fires; multi-file goes through the batch-zip endpoint which
  // emits its own batch-download audit event server-side.
  const handleBatchDownload = async () => {
    const rows = getSelectedRowsForBulk();
    if (rows.length === 0) return;

    if (rows.length === 1) {
      const row = rows.at(0);
      if (!row?.fileKey) return;
      try {
        const fileUrlFromS3 = (
          await S3Utils.getFile(row.fileKey, {
            identityId: row.fileIdentityId,
            level: 'protected',
          })
        ).toString();
        dispatch(
          downloadFiles([
            {
              id: row.rowId,
              fileUrl: fileUrlFromS3,
              fileKey: row.fileKey,
              fileName: getFileNameFromFileKey(row.fileKey),
              emitAuditEvent: true,
              channelId: row.channelId,
              path: row.location,
            },
          ]),
        );
        setSelectedRowIds(new Set());
      } catch (e) {
        const { message } = ensureApiError(e);
        dispatch(alertSnackbar({ errorMessage: message }));
      }
      return;
    }

    try {
      const jobKey = await FilesClient.batchDownload({
        fileIds: rows.map((row) => row.rowId),
        folderIds: [],
      });
      startBatchZipDownload(jobKey, rows.length);
      setSelectedRowIds(new Set());
    } catch (e) {
      const { message } = ensureApiError(e);
      dispatch(
        alertSnackbar({
          errorMessage:
            message ||
            'Your download failed. Please try again or contact support.',
        }),
      );
    }
  };

  const handleBatchDelete = () => {
    setConfirmDeleteOpen(true);
  };

  const handleCloseConfirmDelete = () => {
    if (isBatchDeleting) return;
    setConfirmDeleteOpen(false);
  };

  const handleConfirmBatchDelete = async () => {
    const rows = getSelectedRowsForBulk();
    if (rows.length === 0) {
      setConfirmDeleteOpen(false);
      return;
    }
    setIsBatchDeleting(true);
    try {
      const jobKey = await FilesClient.batchDelete({
        fileIds: rows.map((row) => row.rowId),
        folderIds: [],
      });
      startBatchDeleteProgress(jobKey, rows.length);
      setSelectedRowIds(new Set());
    } catch (e) {
      const { message } = ensureApiError(e);
      dispatch(
        alertSnackbar({
          errorMessage:
            message || t(($) => $.page_files.batch_actions.delete_failed),
        }),
      );
    } finally {
      setIsBatchDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  const bulkActionsConfig = {
    selectedRowIds,
    showDeleteAction: !isClient,
    onToggleRow: handleToggleRow,
    onSelectAll: handleSelectAllRows,
    onClear: handleClearSelection,
    onDownload: handleBatchDownload,
    onDelete: handleBatchDelete,
  };

  const bulkSelectionCount = getSelectedRowsForBulk().length;

  const contextBarTarget = React.useMemo(() => {
    if (!selectedNotification || !selectedNotificationDetails) {
      return null;
    }

    if (selectedNotificationDetails.event === 'customApp.action') {
      return null;
    }

    if (selectedNotificationDetails.senderType === 'CLIENT_USER') {
      return {
        id: selectedNotificationDetails.senderId,
        entityType: 'client' as const,
      };
    }
    if (selectedNotificationDetails.senderType === 'CLIENT_COMPANY') {
      return {
        id: selectedNotificationDetails.senderId,
        entityType: 'company' as const,
      };
    }
    return null;
  }, [selectedNotification, selectedNotificationDetails]);

  const {
    open: contextBarOpen,
    setOpen: setContextBarOpen,
    target,
    clearTarget,
  } = useContextBar(
    contextBarTarget ? { target: contextBarTarget } : undefined,
    { isLoading: selectedNotificationDetailsLoading },
  );

  React.useEffect(() => {
    if (!contextBarTarget && !selectedNotificationDetailsLoading) {
      clearTarget();
    }
  }, [contextBarTarget, clearTarget, selectedNotificationDetailsLoading]);

  /**
   * This function will setup notification details view with data
   * based on selected notification and its type.
   * When it is contract or invoice notification, it will fetch
   * the contract/invoice pdf from s3 to be rendered in the pdf viewer.
   * When it is file upload notification, it will fetch the files related
   * to the notification channel id and then setup the notification table.
   */
  const setupNotificationDetailsView = async () => {
    // Reset the previous preview asset when switching notifications.
    setInvoiceS3AssetUrl('');
    setContractS3AssetUrl('');

    // Getting contract or invoice notification pdf url
    if (
      selectedNotificationDetails &&
      (invoicePreviewNotificationEvents.includes(
        selectedNotificationDetails.event,
      ) ||
        selectedNotificationDetails.event === 'esign.completed' ||
        selectedNotificationDetails.event === 'contract.signed')
    ) {
      switch (selectedNotificationDetails.event) {
        case 'invoice.paid':
        case 'invoice.processing':
        case 'invoice.paymentFailed':
          if (!selectedNotificationDetails?.assetS3Key) {
            break;
          }

          let invoiceNotificationFileUrl = '';
          invoiceNotificationFileUrl = await S3Utils.getFileUrlWithSignedKey(
            selectedNotificationDetails.assetS3Key,
            '/v0/invoices/download/signedUrl',
          );
          setInvoiceS3AssetUrl(invoiceNotificationFileUrl);
          break;
        // todo @akash remove event after backfill
        case 'esign.completed':
        case 'contract.signed':
          let notificationContractS3Key = '';
          notificationContractS3Key = await S3Utils.getFile(
            selectedNotificationDetails.assetS3Key ?? '',
            {
              identityId: selectedNotificationDetails.identityId,
              level: 'protected',
            },
          );
          setContractS3AssetUrl(notificationContractS3Key);
          break;
      }
      // when it is contract or invoice notification then we don't need to
      // setup file notification table data. So we can skip the rest of the logic.
      return;
    }

    setFilesNotificationDetailsDataReady(false);
    // clearing last selected notification data
    setNotificationTableRowData([]);

    // don't do anything if no notification is selected
    if (!selectedNotification || !selectedNotification.channelId) return;

    // only fetch files for file-related notifications
    // also allow legacy grouped e-sig notifications which rely on the file table
    if (
      !isFileNotification &&
      !isFileUploadNotification &&
      (selectedNotification.notificationGroup as NotificationGroups) !==
        NotificationGroups.EsigRequestCompleteNotifications
    )
      return;

    const allChannelFiles = await FilesClient.GetFilesForChannelId(
      selectedNotification.channelId,
    );

    const rowData: NotificationTableRowData[] = [];
    const deletedFilesRowData = [];
    // get id's of file related to notification
    let notificationFileIds: string[] = [];

    // for file upload notifications
    if (isFileUploadNotification) {
      if (selectedNotificationDetails?.items) {
        selectedNotificationDetails.items.forEach(
          (n: InboxNotificationDetailItem) => {
            const fileId = n.resourceId;
            if (fileId === 'default') return; // notification group itself has the 'default' id and we can ignore that

            // checking if the notification file is deleted or not
            if (n.isDeleted) {
              // to indicated a deleted file add a row in notification table
              // with name 'File was deleted'
              deletedFilesRowData.push({
                rowId: fileId,
                name: 'File was deleted',
                location: '',
                isDeleted: true,
              });
              return;
            }

            notificationFileIds.push(fileId);
          },
        );
      }
    } else {
      // for notifications which are not grouped
      // we know there will be only 1 file in those notifications
      const fileId = selectedNotification.data.split('/').at(2);
      // check if the file is deleted
      if (
        selectedNotification.data.includes(DELETED_FILE_NOTIFICATION_STATUS)
      ) {
        deletedFilesRowData.push({
          rowId: fileId,
          name: 'File was deleted',
          location: '',
          isDeleted: true,
        });
      } else if (fileId) {
        notificationFileIds = [fileId];
      }
    }

    const notificationFiles = allChannelFiles.filter((n) =>
      notificationFileIds?.includes(n.id),
    );

    notificationFiles.forEach((file) => {
      const { fields, owner } = file;

      // avoiding folders
      if (fields.fileKey === FOLDER_FILE_KEY) return;
      if (!owner) return;

      let fileExtension = '';
      let fileUrl: string | undefined = undefined;

      // generating file name
      let name = getFileNameFromFileKey(fields.fileKey);
      // except for file links
      // add file extension in file name
      if (!fields.fileUrl) {
        fileExtension = getExtensionFromFileKey(fields.fileKey); // file extension
        name = `
           ${name}${fileExtension}`;
      }

      if (fields.fileUrl) {
        fileExtension = LINK_FILE_EXTENSION;
        ({ fileUrl } = fields);
      }

      // for file upload notification
      // we will show file owner info in the "From" column
      // and for Esign complete notification we will show
      // notification sender user info in "Signed by" column
      const userInfo =
        selectedNotification.notificationGroup === ENTITIES.NOTIFY_FILE_UPLOADED
          ? {
              userName: `${owner?.fields.givenName} ${owner?.fields.familyName}`,
              ...owner,
            }
          : {
              userName: selectedNotification.avatarInfo?.name,
              fields: {
                avatarImageUrl: selectedNotification.avatarInfo?.imageUrl,
                ...selectedNotification.avatarInfo,
              },
            };

      // get file size from notification item data
      const fileData = selectedNotificationDetails?.items?.find(
        (fileNotification) =>
          fileNotification.resourceId === file.id &&
          !fileNotification.isDeleted,
      );
      const fileSize = fileData?.fileSize || 0;
      const fileIdentityId = file?.identityId || '';
      const item = {
        rowId: file.id,
        fileKey: file.fields.fileKey,
        fileIdentityId,
        name,
        fileExtension: fileExtension.replace('.', '').toLowerCase(), // removing "." from extension names e.g ".pdf" > "pdf"
        fileUrl: fileUrl,
        location: file.fields.path || '',
        channelId: selectedNotification.channelId,
        email: userInfo.fields.email,
        fallbackColor: userInfo.fields.fallbackColor,
        picture: userInfo.fields.avatarImageUrl,
        size: fileSize,
        ...userInfo,
      };

      rowData.push(item as NotificationTableRowData);
    });

    setNotificationTableRowData([
      ...rowData,
      ...deletedFilesRowData,
    ] as Array<NotificationTableRowData>);
    setFilesNotificationDetailsDataReady(true);
  };

  /**
   * This effect will setup notification table data
   * it listens to the selected notification details
   * fetched from the API and then setup the table data
   * based on that.
   */
  React.useEffect(() => {
    setupNotificationDetailsView();
  }, [selectedNotificationDetails, selectedNotification?.id]);

  React.useEffect(() => {
    if (
      selectedNotification?.notificationEventType ===
        'internalChat.mentioned' &&
      chatClient
    ) {
      const fetchInternalChannel = async () => {
        const intCh = chatClient.channel(
          'internal',
          selectedNotification.channelId,
        );
        await intCh.watch();
        if (
          intCh &&
          intCh.data &&
          intCh.data.member_count &&
          intCh.data.member_count > 0
        ) {
          let recipientId = '';
          let recipientType: 'client' | 'company' = 'client';
          if (intCh.data && 'clientID' in intCh.data) {
            recipientId = intCh.data.clientID as string;
            recipientType = 'client';
          } else if (intCh.data?.companyID) {
            recipientId = intCh.data?.companyID;
            recipientType = 'company';
          }
          selectedNotification.channelRecipientId = recipientId;
          selectedNotification.channelRecipientType = recipientType;
          setInternalChannelData({
            channel: intCh,
            recipientId,
            recipientType,
            messageId: selectedNotification.resourceId,
          });
        } else {
          // the watch GetStream api creates a channel if it doesn't find one so
          // we assume an error has occurred if the channel has no members and we throw an error
          throw new Error('Channel has no members');
        }
      };
      fetchInternalChannel().catch((e) => {
        console.error(e);
        setInternalChannelError(true);
      });
    }
  }, [chatClient, selectedNotification]);

  // For channel.mentioned notifications, fetch the messaging channel so the
  // breadcrumbs can resolve to "Clients > {name}" or "Companies > {name}".
  // We mutate selectedNotification AND call setMessagingChannelData so
  // NotificationDetailsContainer re-renders once the async fetch resolves.
  React.useEffect(() => {
    if (
      selectedNotification?.notificationEventType !== 'channel.mentioned' ||
      !chatClient ||
      !selectedNotification.channelId
    ) {
      return;
    }
    // Skip refetch if we already have the data for this notification.
    if (messagingChannelData?.notificationId === selectedNotification.id) {
      return;
    }
    let cancelled = false;
    setMessagingChannelError(false);
    const fetchMessagingChannel = async () => {
      const ch = chatClient.channel(
        'messaging',
        selectedNotification.channelId,
      );
      await ch.watch();
      if (cancelled) return;
      if (!ch.data?.member_count) {
        throw new Error('Channel has no members');
      }
      let recipientId = '';
      let recipientType: 'client' | 'company' | undefined;
      if ('clientID' in ch.data && ch.data.clientID) {
        recipientId = ch.data.clientID as string;
        recipientType = 'client';
      } else if (
        'companyID' in ch.data &&
        (ch.data as { companyID?: string }).companyID
      ) {
        recipientId = (ch.data as { companyID?: string }).companyID as string;
        recipientType = 'company';
      }
      if (!recipientType || !selectedNotification.id) return;
      selectedNotification.channelRecipientId = recipientId;
      selectedNotification.channelRecipientType = recipientType;
      setMessagingChannelData({
        notificationId: selectedNotification.id,
        recipientId,
        recipientType,
      });
    };
    fetchMessagingChannel().catch((e) => {
      console.error(e);
      if (!cancelled) setMessagingChannelError(true);
    });
    return () => {
      cancelled = true;
    };
  }, [chatClient, selectedNotification, messagingChannelData?.notificationId]);

  const renderInProductBodyCard = (
    body: string,
    ctaLabel?: string,
    onClick?: () => void,
  ) => (
    <div style={{ height: '100%' }}>
      <RegularCardBox>
        <div className="p-6">
          <MemoBaseTypography fontType="13Regular">{body}</MemoBaseTypography>
          {ctaLabel && onClick ? (
            <Button
              variant="outlined"
              htmlId="in-product-body-cta"
              className="mt-[17px] no-underline"
              onClick={onClick}
            >
              <MemoBaseTypography fontType="12Medium">
                {ctaLabel}
              </MemoBaseTypography>
            </Button>
          ) : null}
        </div>
      </RegularCardBox>
    </div>
  );

  // customApp.buildComplete has no deployed-app URL yet (it fires at build-
  // complete, before deploy) — Open app always targets the builder session.
  // appUrl is the primary source; the appBuilderId+sessionId fallback only
  // matters for notifications created before the appUrl cta param existed.
  const appReadyCtaParams =
    selectedNotification?.notificationEventType === 'customApp.buildComplete' &&
    !isAppReadyDetailsStale
      ? selectedNotificationDetails?.deliveryTargets?.inProduct?.ctaParams
      : undefined;
  const appReadyUrl =
    appReadyCtaParams?.appUrl ||
    (appReadyCtaParams?.appBuilderId && appReadyCtaParams?.sessionId
      ? `${APP_BUILDER_SESSION_PATH_PREFIX}${appReadyCtaParams.appBuilderId}/${appReadyCtaParams.sessionId}`
      : undefined);
  const handleOpenApp = appReadyUrl
    ? () => {
        navigate(appReadyUrl);
      }
    : undefined;

  const renderAppReadyContent = (title: string) => {
    const isClientFacing = isAppReadyClientFacing(appReadyCtaParams);
    const steps = isClientFacing
      ? [
          t(($) => $.page_inbox.app_ready.client_facing_step_1),
          t(($) => $.page_inbox.app_ready.client_facing_step_2),
          t(($) => $.page_inbox.app_ready.client_facing_step_3),
          t(($) => $.page_inbox.app_ready.client_facing_step_4),
        ]
      : [
          t(($) => $.page_inbox.app_ready.internal_step_1),
          t(($) => $.page_inbox.app_ready.internal_step_2),
          t(($) => $.page_inbox.app_ready.internal_step_3),
        ];

    return (
      <div className="px-8 py-6 max-w-[640px]">
        <Heading size="xl" className="text-black-heading mb-2">
          {title}
        </Heading>
        <Body size="lg" className="text-gray-small mb-4">
          {t(($) => $.page_inbox.app_ready.intro)}
        </Body>
        <ol className="list-decimal pl-5 space-y-2">
          {steps.map((step, index) => (
            <li key={index}>
              <MemoBaseTypography fontType="13Regular">
                {renderEmphasis(step)}
              </MemoBaseTypography>
            </li>
          ))}
        </ol>
      </div>
    );
  };

  const notificationDetailsContentRenderer = () => {
    if (!selectedNotification) return null;
    // this will be replaced by checking event type
    switch (selectedNotification.notificationEventType) {
      // TODO(backfill-9984): remove the forms.completed case. This was an old name that is not used anymore
      case 'formResponse.completed':
      case 'forms.completed': {
        const { resourceId: formId, refId: responseRefId } =
          selectedNotification;

        return (
          <NotificationDetailsContainer
            selectedNotificationDetails={selectedNotificationDetails}
            selectedNotification={selectedNotification}
          >
            <FormResponseDetail
              detailOptions={{
                formId,
                responseRefId,
              }}
              isNotificationPreview
            />
          </NotificationDetailsContainer>
        );
      }
      case 'invoice.paid':
      case 'invoice.processing':
      case 'invoice.paymentFailed': {
        return (
          <>
            <NotificationDetailsContainer
              selectedNotification={selectedNotification}
              selectedNotificationDetails={selectedNotificationDetails}
            >
              {invoiceS3AssetUrl && (
                <NotificationPdfViewer
                  pdfUrl={invoiceS3AssetUrl}
                  previewContainerRef={previewContainerRef}
                />
              )}
            </NotificationDetailsContainer>
          </>
        );
      }
      case 'refund.processed':
      case 'refund.failed': {
        const inProductBody =
          selectedNotificationDetails?.deliveryTargets?.inProduct?.body;
        const body =
          inProductBody ||
          (selectedNotification.notificationEventType === 'refund.failed'
            ? 'The refund could not be processed. Please try again or contact support if the issue persists.'
            : 'The refund has been processed. The funds will appear on the statement within 5-10 business days.');
        const invoiceId =
          selectedNotificationDetails?.deliveryTargets?.inProduct?.ctaParams
            ?.invoiceId || selectedNotification.resourceId;
        const ctaLabel =
          selectedNotification.notificationEventType === 'refund.failed'
            ? 'Retry refund'
            : 'View invoice';

        return (
          <NotificationDetailsContainer
            selectedNotification={selectedNotification}
            selectedNotificationDetails={selectedNotificationDetails}
          >
            {renderInProductBodyCard(
              body,
              invoiceId ? ctaLabel : undefined,
              invoiceId
                ? () => {
                    navigate(
                      `${PAYMENTS_INVOICES_PAGE.path}/details?invoiceId=${encodeURIComponent(invoiceId)}`,
                    );
                  }
                : undefined,
            )}
          </NotificationDetailsContainer>
        );
      }
      case 'internalChat.mentioned': {
        return (
          <>
            {internalChannelData?.channel &&
              internalChannelData?.recipientId &&
              internalChannelData?.recipientType && (
                <AssistantProvider>
                  <NotificationDetailsContainer
                    selectedNotification={selectedNotification}
                    selectedNotificationDetails={selectedNotificationDetails}
                  >
                    <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                      <InternalChat
                        entityType={internalChannelData.recipientType}
                        entityId={internalChannelData.recipientId}
                        internalChat={internalChannelData.channel}
                        messageId={internalChannelData.messageId}
                      />
                    </div>
                  </NotificationDetailsContainer>
                </AssistantProvider>
              )}
            {internalChannelError && (
              <InternalChatError error="Can't load this notification" />
            )}
          </>
        );
      }

      case 'files.new':
      case 'files.created':
      case 'esign.completed':
      case 'contract.signed':
        const notificationGroup =
          selectedNotification.notificationGroup as NotificationGroups;
        // notificationGroup is only present in the older e-signature notifications
        // todo(notification-center): remove the check for notificationGroup once flag is removed
        if (notificationGroup) {
          return (
            <>
              <NotificationDetailsContainer
                selectedNotificationDetails={selectedNotificationDetails}
                selectedNotification={selectedNotification}
              >
                <div style={{ height: '100%' }}>
                  <InboxNotificationTable
                    rowData={notificationTableRowData}
                    isLoading={isNotificationContentLoading}
                    // since old e-sig notifications do not have a notificationEventType
                    // and since now the inbox notification table accepts a notificationEventType
                    // then we need to map the notificationGroup to a notificationEventType.
                    notificationEventType={
                      LegacyNotificationTypeToNotificationGroup[
                        selectedNotification.notificationGroup as NotificationGroups
                      ] as NotificationEventType
                    }
                    bulkActions={bulkActionsConfig}
                  />
                </div>
              </NotificationDetailsContainer>
            </>
          );
        }

        return (
          <>
            <NotificationDetailsContainer
              selectedNotificationDetails={selectedNotificationDetails}
              selectedNotification={selectedNotification}
            >
              {contractS3AssetUrl && (
                <NotificationPdfViewer
                  pdfUrl={contractS3AssetUrl}
                  previewContainerRef={previewContainerRef}
                />
              )}
            </NotificationDetailsContainer>
          </>
        );

      case 'channel.mentioned': {
        if (!EnableMessageAppNotification || !selectedNotification.channelId) {
          return null;
        }
        if (messagingChannelError) {
          return (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-2/3 flex flex-col items-center text-center">
                <Icon
                  icon="Warning"
                  className="w-7 h-7 text-warning-primary mb-[18px]"
                />
                <Heading size="xl" className="text-black-heading mb-2">
                  Can&apos;t load this notification
                </Heading>
                <Body size="lg" className="text-gray-small">
                  Something went wrong. Please try refreshing the page.
                </Body>
              </div>
            </div>
          );
        }
        return (
          <AssistantProvider>
            <NotificationDetailsContainer
              selectedNotification={selectedNotification}
              selectedNotificationDetails={selectedNotificationDetails}
              className="pt-5 px-5"
            >
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                <ChannelMention
                  channelId={selectedNotification.channelId}
                  messageId={selectedNotification.resourceId}
                />
              </div>
            </NotificationDetailsContainer>
          </AssistantProvider>
        );
      }

      case 'customApp.buildComplete':
        return (
          <NotificationDetailsContainer
            selectedNotification={selectedNotification}
            selectedNotificationDetails={selectedNotificationDetails}
          >
            {renderAppReadyContent(selectedNotification.description)}
          </NotificationDetailsContainer>
        );

      case 'customApp.action':
        const appName =
          (extensionsSettings &&
            selectedNotificationDetails &&
            extensionsSettings[selectedNotificationDetails?.resourceId]
              ?.name) ||
          '';
        const queryParams = new URLSearchParams({
          id: selectedNotificationDetails?.resourceId ?? '',
          ...(selectedNotificationDetails?.deliveryTargets?.inProduct
            ?.ctaParams ?? {}),
        });
        return (
          <>
            <NotificationDetailsContainer
              selectedNotification={selectedNotification}
              selectedNotificationDetails={selectedNotificationDetails}
            >
              {notificationUrl ? (
                <iframe src={notificationUrl} className="w-full h-full" />
              ) : (
                renderInProductBodyCard(
                  selectedNotificationDetails?.deliveryTargets?.inProduct
                    ?.body ?? '',
                  `Open ${appName}`,
                  () => {
                    navigate(
                      `${CLIENT_APPS_PAGE.path}?${queryParams.toString()}`,
                    );
                  },
                )
              )}
            </NotificationDetailsContainer>
          </>
        );

      default:
        return null;
    }
  };

  const showTimeline =
    selectedNotificationDetails &&
    selectedNotificationDetails.timeline &&
    selectedNotificationDetails.timeline.length > 0;

  // this method handle deleting a notification from
  // the notification details page toolbar.
  const handleDeleteNotification = () => {
    deleteNotification();
  };

  const { handleExportForm: exportForm } = useExportFormResponse(
    getFormCompletedResourceIds(selectedNotification),
  );

  // this method handles exporting a notification form
  // TODO: implement this method later
  const handleExportForm = () => {
    exportForm({
      ref: selectedNotification?.refId || '',
    });
  };

  const handleDownloadNotificationAsset = async () => {
    if (!selectedNotification) {
      return;
    }
    if (selectedNotificationDetails?.assetS3Key) {
      const isInvoiceAssetEvent = invoicePreviewNotificationEvents.includes(
        selectedNotification.notificationEventType,
      );
      const fileName =
        selectedNotification.notificationEventType === 'invoice.paid'
          ? `Invoice-${
              selectedNotificationDetails.additionalFields?.invoiceNumber ||
              selectedNotificationDetails.resourceId
            }`
          : selectedNotification.notificationEventType === 'refund.processed'
            ? `Receipt-${
                selectedNotificationDetails.additionalFields?.invoiceNumber ||
                selectedNotificationDetails.resourceId
              }`
            : getFileNameFromFileKey(selectedNotificationDetails.assetS3Key);

      // todo @akash remove this once events are backfilled
      const isContractSignedEvent =
        selectedNotificationDetails.event === 'esign.completed' ||
        selectedNotificationDetails.event === 'contract.signed';
      await FileUtils.downloadFileFromUrl(
        isContractSignedEvent || !isInvoiceAssetEvent
          ? contractS3AssetUrl
          : invoiceS3AssetUrl,
        selectedNotificationDetails.assetS3Key,
        fileName,
      );
    }
  };

  // when no notification is selected
  // render empty page
  if (!selectedNotification) return <InboxEmptyState />;

  return (
    <div
      className={clsx('h-[calc(100%-60px)] w-full p-0')}
      data-testid="inbox-selected-notification"
    >
      <ChannelContentToolbar
        title={selectedNotification.description}
        onBackClick={() => {
          history.replace({
            search: '',
          });
          // In `InboxSidebar` component, here's a useEffect that runs again and set the selectedNotification back to active item id because state is async and there's still active channelId
          // So we need to wait for query to reset before setting selectedNotification to null
          setTimeout(() => {
            setSelectedNotification(null);
          }, 100);
        }}
        AdditionalSlotsComponents={[
          <NotificationDetailsHeaderActions
            key="notification-details-header-actions"
            onDeleteNotificationClick={handleDeleteNotification}
            onExportFormClick={handleExportForm}
            onDownloadAssetClick={handleDownloadNotificationAsset}
            onOpenAppClick={handleOpenApp}
            selectedNotification={selectedNotification}
            s3AssetUrl={
              invoicePreviewNotificationEvents.includes(
                selectedNotification.notificationEventType,
              )
                ? invoiceS3AssetUrl
                : contractS3AssetUrl
            }
          />,
        ].concat(
          showTimeline && target
            ? [
                <RightSidebarToggle
                  isSidebarOpen={contextBarOpen}
                  key="timeline-toggle"
                  data-testid="timeline-toggle"
                  className="border border-solid border-[#F1F3F8] h-7 w-7 bg-white sm:ml-3.5 ml-2 sm:hidden"
                  onToggle={(isOpen) => {
                    setContextBarOpen(isOpen);
                  }}
                />,
              ]
            : [],
        )}
      />
      <LoadingWrapper
        fullHeight
        isLoading={isNotificationContentLoading}
        hideContentWhileLoading={!isTableBasedNotification}
      >
        <div
          className={clsx(
            'h-full w-full overflow-y-scroll flex flex-col gap-4',
            isFileNotification || isChannelMentioned ? 'p-0' : 'p-5',
          )}
          ref={previewContainerRef}
        >
          {notificationDetailsContentRenderer()}
        </div>
      </LoadingWrapper>
      {confirmDeleteOpen && (
        <ActionButtonModal
          open={confirmDeleteOpen}
          isDeleteModal
          onClose={handleCloseConfirmDelete}
          positiveAction
          onPositiveActionPerformed={handleConfirmBatchDelete}
          isLoading={isBatchDeleting}
          disableSuccessButton={isBatchDeleting}
          title={t(($) => $.page_files.batch_actions.delete_modal_title, {
            count: bulkSelectionCount,
          })}
          positiveActionText={t(
            ($) => $.page_files.batch_actions.delete_confirm,
          )}
          cancelActionText={t(($) => $.page_files.batch_actions.delete_cancel)}
          mainComponent={t(
            ($) => $.page_files.batch_actions.delete_modal_body,
            { count: bulkSelectionCount },
          )}
        />
      )}
    </div>
  );
};
